// Mock data for disposal batches

export interface DisposalItem {
  id: string;
  assetId: string;
  assetCode: string;
  assetDescription: string;
  category: string;
  location: string;
  currentCost: number;
  currentDepreciation: number;
  currentNBV: number;
  historicalCost: number;
  historicalDepreciation: number;
  historicalNBV: number;
  disposalCost: number;
  disposalDepreciation: number;
  disposalNBV: number;
  proceedsValue: number;
  gainLoss: number;
  notes?: string;
}

export interface DisposalBatch {
  id: string;
  batchReference: string;
  auditNumber?: string;
  dateOfRun: string;
  periodType: 'current' | 'service';
  period?: number;
  year?: number;
  status: 'In Progress' | 'Completed';
  disposalReasonId?: string;
  disposalReasonCode?: string;
  disposalReasonDescription?: string;
  disposeOfMode: 'value' | 'percentage' | 'quantity';
  depreciationHandling?: 'reverse' | 'dispose';
  notes: string;
  items: DisposalItem[];
  preparedBy: string;
  preparedDate: string;
  postedBy?: string;
  postedDate?: string;
  totalDisposalValue: number;
  totalProceedsValue: number;
  totalGainLoss: number;
  currency: string;
  createdBy: string;
  createdDate: string;
  modifiedBy: string;
  modifiedDate: string;
}

export const mockDisposalBatches: DisposalBatch[] = [
  {
    id: 'disp-batch-001',
    batchReference: 'DISP-2024-001',
    auditNumber: 'AUD-2024-4001',
    dateOfRun: '2024-01-15',
    periodType: 'current',
    period: 1,
    year: 2024,
    status: 'Completed',
    disposalReasonId: 'reason-001',
    disposalReasonCode: 'SOLD',
    disposalReasonDescription: 'Asset sold to third party',
    disposeOfMode: 'value',
    depreciationHandling: 'dispose',
    notes: 'End of year disposal of obsolete equipment',
    items: [
      {
        id: 'disp-item-001',
        assetId: 'asset-001',
        assetCode: 'COMP-001',
        assetDescription: 'Dell Laptop - Finance Dept',
        category: 'IT Equipment',
        location: 'London Office',
        currentCost: 1200.00,
        currentDepreciation: 800.00,
        currentNBV: 400.00,
        historicalCost: 1200.00,
        historicalDepreciation: 800.00,
        historicalNBV: 400.00,
        disposalCost: 1200.00,
        disposalDepreciation: 800.00,
        disposalNBV: 400.00,
        proceedsValue: 300.00,
        gainLoss: -100.00,
        notes: 'Sold at auction',
      },
      {
        id: 'disp-item-002',
        assetId: 'asset-002',
        assetCode: 'FURN-045',
        assetDescription: 'Office Desk - Meeting Room 3',
        category: 'Furniture',
        location: 'London Office',
        currentCost: 800.00,
        currentDepreciation: 600.00,
        currentNBV: 200.00,
        historicalCost: 800.00,
        historicalDepreciation: 600.00,
        historicalNBV: 200.00,
        disposalCost: 800.00,
        disposalDepreciation: 600.00,
        disposalNBV: 200.00,
        proceedsValue: 150.00,
        gainLoss: -50.00,
        notes: '',
      },
    ],
    preparedBy: 'John Smith',
    preparedDate: '2024-01-15T09:00:00Z',
    postedBy: 'John Smith',
    postedDate: '2024-01-15T10:30:00Z',
    totalDisposalValue: 2000.00,
    totalProceedsValue: 450.00,
    totalGainLoss: -150.00,
    currency: 'GBP',
    createdBy: 'John Smith',
    createdDate: '2024-01-15T09:00:00Z',
    modifiedBy: 'John Smith',
    modifiedDate: '2024-01-15T09:00:00Z',
  },
  {
    id: 'disp-batch-002',
    batchReference: 'DISP-2024-002',
    dateOfRun: '2024-02-20',
    periodType: 'current',
    period: 2,
    year: 2024,
    status: 'Completed',
    disposalReasonId: 'reason-002',
    disposalReasonCode: 'SCRAP',
    disposalReasonDescription: 'Asset scrapped - end of useful life',
    disposeOfMode: 'value',
    depreciationHandling: 'dispose',
    notes: 'Scrapped obsolete vehicles',
    items: [
      {
        id: 'disp-item-003',
        assetId: 'asset-003',
        assetCode: 'VEH-012',
        assetDescription: 'Ford Transit Van - Registration AB12 CDE',
        category: 'Vehicles',
        location: 'Birmingham Depot',
        currentCost: 18000.00,
        currentDepreciation: 16000.00,
        currentNBV: 2000.00,
        historicalCost: 18000.00,
        historicalDepreciation: 16000.00,
        historicalNBV: 2000.00,
        disposalCost: 18000.00,
        disposalDepreciation: 16000.00,
        disposalNBV: 2000.00,
        proceedsValue: 2500.00,
        gainLoss: 500.00,
        notes: 'Sold to dealer',
      },
    ],
    preparedBy: 'Sarah Johnson',
    preparedDate: '2024-02-20T11:00:00Z',
    postedBy: 'Sarah Johnson',
    postedDate: '2024-02-20T14:00:00Z',
    totalDisposalValue: 18000.00,
    totalProceedsValue: 2500.00,
    totalGainLoss: 500.00,
    currency: 'GBP',
    createdBy: 'Sarah Johnson',
    createdDate: '2024-02-20T11:00:00Z',
    modifiedBy: 'Sarah Johnson',
    modifiedDate: '2024-02-20T11:00:00Z',
  },
  {
    id: 'disp-batch-003',
    batchReference: 'DISP-2024-003',
    dateOfRun: '2024-03-10',
    periodType: 'current',
    period: 3,
    year: 2024,
    status: 'In Progress',
    disposalReasonId: 'reason-001',
    disposalReasonCode: 'SOLD',
    disposalReasonDescription: 'Asset sold to third party',
    disposeOfMode: 'percentage',
    notes: 'Partial disposal of building improvements',
    items: [
      {
        id: 'disp-item-004',
        assetId: 'asset-004',
        assetCode: 'PROP-001',
        assetDescription: 'Office Building - Floor 2 Refurbishment',
        category: 'Property Improvements',
        location: 'Manchester Office',
        currentCost: 50000.00,
        currentDepreciation: 10000.00,
        currentNBV: 40000.00,
        historicalCost: 50000.00,
        historicalDepreciation: 10000.00,
        historicalNBV: 40000.00,
        disposalCost: 25000.00,
        disposalDepreciation: 5000.00,
        disposalNBV: 20000.00,
        proceedsValue: 0.00,
        gainLoss: -20000.00,
        notes: 'Partial disposal - 50% of asset',
      },
    ],
    preparedBy: 'Michael Brown',
    preparedDate: '2024-03-10T08:30:00Z',
    totalDisposalValue: 25000.00,
    totalProceedsValue: 0.00,
    totalGainLoss: -20000.00,
    currency: 'GBP',
    createdBy: 'Michael Brown',
    createdDate: '2024-03-10T08:30:00Z',
    modifiedBy: 'Michael Brown',
    modifiedDate: '2024-03-10T08:30:00Z',
  },
];
